/**
 * Builds the "Values" configuration tab for a form widget, supporting both static values
 * and dynamic value providers (with optional filtering logic).
 *
 * @param {WidgetProps<any, any>} props - The widget props containing context and configuration.
 * @param {object} [params] - Optional configuration parameters.
 * @param {'always' | 'if-show-description'} [params.description] - Controls when the "Description" field appears per value.
 * @param {boolean} [params.fixed] - If true, includes a "Fixed" checkbox column per value.
 * @param {DefaultFormWidgetConfigTypes[]} [params.children] - Additional custom config fields to include in the tab.
 * @param {(data: any) => any} [params.onLoad] - Optional transformation of the data on tab load.
 * @param {(data: any) => any} [params.onSubmit] - Optional transformation of the data on tab submit.
 * @returns {EditorTab} The configured "Values" editor tab definition.
 */
export function buildValuesTab(props, params) {
    var _a, _b;
    const valueProviders = (_a = props.rootParams.valueProviders) !== null && _a !== void 0 ? _a : [];
    const parentConfig = (_b = props.parentProps) === null || _b === void 0 ? void 0 : _b.config;
    const siblings = [];
    if (parentConfig) {
        const parentWidget = props.widgets.find((w) => w.type === parentConfig.type);
        if (parentWidget && parentWidget.childConfigs) {
            parentWidget.childConfigs(parentConfig).forEach((siblingConfig) => {
                if (siblingConfig !== props.config && siblingConfig.key) {
                    const component = '^' + siblingConfig.key;
                    switch (siblingConfig.type) {
                        case 'form.checkbox':
                            siblings.push({ value: component, label: 'Checkbox: ' + siblingConfig.label });
                            break;
                        case 'form.number':
                            siblings.push({ value: component, label: 'Number: ' + siblingConfig.label });
                            break;
                        case 'form.text':
                            siblings.push({ value: component, label: 'Text: ' + siblingConfig.label });
                            break;
                        case 'form.radio':
                            siblings.push({ value: component, label: 'Radio: ' + siblingConfig.label });
                            break;
                        case 'form.select':
                            siblings.push({ value: component, label: 'Select: ' + siblingConfig.label });
                            break;
                    }
                }
            });
        }
        siblings.push({ value: '$', label: 'Other' });
    }
    const children = valueProviders.length > 0
        ? [
            {
                type: 'form.radio',
                key: 'valuesType',
                values: [
                    { value: 'values', label: 'Values' },
                    { value: 'valueProvider', label: 'Value Provider' },
                ],
                style: {
                    inline: true,
                },
            },
            {
                type: 'form.panel',
                children: createValuesDataGrid(props, params === null || params === void 0 ? void 0 : params.description, params === null || params === void 0 ? void 0 : params.fixed),
                hidden: {
                    when: '^valuesType',
                    dataEq: 'valueProvider',
                },
            },
            {
                type: 'form.select',
                key: 'valueProvider.type',
                label: 'Value Provider',
                values: valueProviders.map((valueProvider) => ({
                    value: valueProvider.type,
                    label: valueProvider.label,
                })),
                hidden: {
                    when: '^valuesType',
                    dataNotEq: 'valueProvider',
                },
            },
            {
                type: 'form.panel',
                hidden: {
                    when: '^valuesType',
                    dataNotEq: 'valueProvider',
                },
                children: [
                    {
                        type: 'form.radio',
                        key: 'valueProvider.paramsFromFilter',
                        label: 'Filter Values',
                        values: [
                            { value: undefined, label: 'None' },
                            { value: 'component', label: 'Other Component' },
                            { value: 'formData', label: 'Form Data' },
                            { value: 'formParams', label: 'Form Params' },
                            { value: 'paramsFunction', label: 'Params Function' },
                        ],
                        style: {
                            inline: true,
                        },
                    },
                    {
                        type: 'form.panel',
                        hidden: {
                            when: '^valueProvider.paramsFromFilter',
                            dataNotEq: 'component',
                        },
                        children: [
                            {
                                type: 'form.select',
                                key: 'valueProvider.paramsFromSibling',
                                label: 'Component',
                                values: siblings,
                            },
                            {
                                type: 'form.text',
                                key: 'valueProvider.paramsFromCustom',
                                label: 'Component Query',
                                hidden: {
                                    when: '^valueProvider.paramsFromSibling',
                                    dataNotEq: '$',
                                },
                            },
                        ],
                    },
                    {
                        type: 'form.text',
                        key: 'valueProvider.paramsFromFormData',
                        hidden: {
                            when: '^valueProvider.paramsFromFilter',
                            dataNotEq: 'formData',
                        },
                        label: 'Form Data Selector',
                    },
                    {
                        type: 'form.text',
                        key: 'valueProvider.paramsFromFormParams',
                        hidden: {
                            when: '^valueProvider.paramsFromFilter',
                            dataNotEq: 'formParams',
                        },
                        label: 'Form Params Selector',
                    },
                    {
                        type: 'form.text',
                        key: 'valueProvider.paramsFunction',
                        hidden: {
                            when: '^valueProvider.paramsFromFilter',
                            dataNotEq: 'paramsFunction',
                        },
                        label: 'Form Params Function',
                    },
                ],
            },
        ]
        : createValuesDataGrid(props, params === null || params === void 0 ? void 0 : params.description, params === null || params === void 0 ? void 0 : params.fixed);
    if (params === null || params === void 0 ? void 0 : params.children) {
        children.push(...params.children);
    }
    const onLoad = (data) => {
        var _a;
        data.valuesType = 'values';
        if (!data.values && ((_a = data.valueProvider) === null || _a === void 0 ? void 0 : _a.type)) {
            data.valuesType = 'valueProvider';
            if (data.valueProvider.paramsFrom) {
                data.valueProvider.paramsFromFilter = 'component';
                if (siblings.find((sibling) => sibling.value === data.valueProvider.paramsFrom)) {
                    data.valueProvider.paramsFromSibling = data.valueProvider.paramsFrom;
                }
                else {
                    data.valueProvider.paramsFromSibling = '$';
                    data.valueProvider.paramsFromCustom = data.valueProvider.paramsFrom;
                }
            }
            else if (data.valueProvider.paramsFromFormData) {
                data.valueProvider.paramsFromFilter = 'formData';
            }
            else if (data.valueProvider.paramsFromFormParams) {
                data.valueProvider.paramsFromFilter = 'formParams';
            }
        }
        if (params === null || params === void 0 ? void 0 : params.onLoad) {
            data = params.onLoad(data);
        }
        return data;
    };
    const onSubmit = (data) => {
        if (data.valuesType === 'values') {
            delete data.valueProvider;
        }
        else {
            data.values = undefined;
            if (data.valueProvider) {
                if (data.valueProvider.paramsFromFilter === 'component') {
                    if (data.valueProvider.paramsFromSibling !== '$') {
                        data.valueProvider.paramsFrom = data.valueProvider.paramsFromSibling;
                    }
                    else {
                        data.valueProvider.paramsFrom = data.valueProvider.paramsFromCustom;
                    }
                    if (!data.valueProvider.paramsFrom) {
                        delete data.valueProvider.paramsFrom;
                    }
                    delete data.valueProvider.paramsFromFormData;
                    delete data.valueProvider.paramsFromFormParams;
                }
                else if (data.valueProvider.paramsFromFilter === 'formData') {
                    delete data.valueProvider.paramsFrom;
                    delete data.valueProvider.paramsFromFormParams;
                }
                else if (data.valueProvider.paramsFromFilter === 'formParams') {
                    delete data.valueProvider.paramsFrom;
                    delete data.valueProvider.paramsFromFormData;
                }
                else {
                    delete data.valueProvider.paramsFrom;
                }
                delete data.valueProvider.paramsFromFilter;
                delete data.valueProvider.paramsFromSibling;
                delete data.valueProvider.paramsFromCustom;
            }
        }
        data.valuesType = undefined;
        if (data.values && data.values.length > 0 && !data.showDescription) {
            data.values.forEach((value) => delete value.description);
        }
        if (params === null || params === void 0 ? void 0 : params.onSubmit) {
            data = params.onSubmit(data);
        }
        return data;
    };
    return { label: 'Values', children, onLoad, onSubmit };
}
function createValuesDataGrid(props, description, fixed) {
    const valuesChildren = [
        { type: 'form.text', key: 'value', label: 'Value', validate: { required: true } },
        { type: 'form.text', key: 'label', label: 'Label', validate: { required: true } },
    ];
    if (props.widget.type == 'form.multiSelect' || props.widget.type == 'form.select') {
        valuesChildren.push({ type: 'form.text', key: 'group', label: 'Group' });
    }
    const valuesDescription = { type: 'form.text', key: 'description', label: 'Description' };
    const valuesDisabled = { type: 'form.checkbox', key: 'disabled', label: 'Disabled' };
    const valuesFixed = { type: 'form.checkbox', key: 'fixed', label: 'Fixed' };
    const valuesDataGrid = {
        type: 'form.dataGrid',
        key: 'values',
        label: 'Values',
        children: [...valuesChildren, valuesDisabled, ...(fixed ? [valuesFixed] : [])],
    };
    if (!description) {
        return [valuesDataGrid];
    }
    const valuesDataGridDescriptionAlways = {
        type: 'form.dataGrid',
        key: 'values',
        label: 'Values',
        children: [...valuesChildren, valuesDescription, valuesDisabled],
    };
    if (description === 'always') {
        return [valuesDataGridDescriptionAlways];
    }
    else {
        return [
            {
                type: 'form.checkbox',
                key: 'showDescription',
                label: 'Show Description',
            },
            Object.assign(Object.assign({}, valuesDataGrid), { hidden: {
                    when: '^/showDescription',
                    dataEq: true,
                } }),
            Object.assign(Object.assign({}, valuesDataGridDescriptionAlways), { hidden: {
                    when: '^/showDescription',
                    dataNotEq: true,
                } }),
        ];
    }
}
